import { useState, useRef, useEffect } from "react";
import { Button, Box, Stack, IconButton, Grid, ToggleButton, Typography } from "@mui/material";
import update from 'immutability-helper'
import KeyboardDoubleArrowUpIcon from '@mui/icons-material/KeyboardDoubleArrowUp';
import KeyboardDoubleArrowDownIcon from '@mui/icons-material/KeyboardDoubleArrowDown';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import UpdateNoteTiming from "./UpdateNoteTiming";
import AddIcon from '@mui/icons-material/Add';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import FretboardCard from "./FretboardCard";
import SVGAnimationComponent from "./SVGAnimationComponent";
import ShowNoteTiming from "./ShowNoteTiming";
import styles from "../styles"

/**
 * Renders the FretboardMenu component.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {Array} props.drawings - The collection of fretboard drawings.
 * @param {Function} props.replaceSelectedFrets - The function to replace selected frets.
 * @param {Function} props.handleSaveButtonClick - The function to handle save button click.
 * @param {Function} props.updateDrawings - The function to update the collection of drawings.
 * @param {boolean} props.noEdit - Flag indicating whether editing is allowed or not.
 * @param {number} props.width - The width of the fretboard.
 * @param {boolean} props.premium - Flag indicating whether the user has premium access or not.
 * @param {boolean} props.animate - Flag indicating whether animation is enabled or not.
 * @param {Function} props.handleAnimateToggle - The function to handle animate toggle.
 * @param {Function} props.updateDrawing - The function to update a drawing.
 * @param {Function} props.cancelReplaceSelectedFrets - The function to cancel replacing selected frets.
 * @param {Function} props.handleDownloadImage - The function to handle download image.
 * @param {string} props.backgroundColor - The background color of the fretboard.
 * 
 * @returns {JSX.Element} The rendered FretboardMenu component.
 * 
 * @function handleCardClick - Handles the event when a card is clicked.
 * @param {Object} drawing - The drawing object associated with the clicked card.
 * @param {number} index - The index of the clicked card in the drawings array.
 * 
 * @function handleMoveUp - Handles the event when the move up button is clicked.
 * @param {number} index - The index of the card to be moved up in the drawings array.
 * 
 * @function handleMoveDown - Handles the event when the move down button is clicked.
 * @param {number} index - The index of the card to be moved down in the drawings array.
 * 
 * @function handleDeleteFretboard - Handles the event when the delete button is clicked.
 * @param {number} index - The index of the card to be deleted in the drawings array.
 * 
 * @function handleEditFretboard - Handles the event when the edit button is clicked.
 * @param {number} index - The index of the card to be edited in the drawings array.
 * @param {Object} drawing - The drawing object associated with the card to be edited.
 * 
 * @function handleCancelEdits - Cancels any ongoing edits.
 * 
 * @function handleUpdateDiagram - Updates the diagram at the specified index.
 * @param {number} index - The index of the diagram to be updated in the drawings array.
 * 
 * @function handleCloseDialog - Closes the dialog box.
 */
function FretboardMenu({
    drawings,
    replaceSelectedFrets,
    handleSaveButtonClick,
    updateDrawings,
    noEdit,
    width,
    height,
    premium,
    animate,
    handleAnimateToggle,
    updateDrawing,
    cancelReplaceSelectedFrets,
    handleDownloadImage,
    backgroundColor, }) {
    const [isEditing, setIsEditing] = useState(-1); // the index of the fretboard being edited
    const [validDrawings, setValidDrawings] = useState([]);
    const [position, setPosition] = useState(0);
    const [showAnimation, setShowAnimation] = useState(true);
    const [openDialog, setOpenDialog] = useState(false);
    const [showAlternates, setShowAlternates] = useState(false);


    useEffect(() => {
        if (Array.isArray(drawings.collection)) {
            setValidDrawings(drawings.collection);
        }
        setPosition(drawings.current);
    }, [drawings]);

    useEffect(() => {
        setShowAnimation(animate);
    }, [animate]);

    useEffect(() => {
        //if not noEdit, and there are no drawings, don't show Alternates
        if (noEdit && validDrawings.length === 0) {
            setShowAlternates(false);
        }
        else {
            setShowAlternates(true);
        }
    }, []);

    const handleCardClick = (drawing, index) => {
        setIsEditing(index);
        //const returnData = drawing.data;
        replaceSelectedFrets(drawing);
        setPosition(index);
    };

    const handleMoveUp = (index) => {
        if (index > 0) {
            const newSelections = update(validDrawings, {
                $splice: [[index - 1, 2, validDrawings[index], validDrawings[index - 1]]],
            });
            setValidDrawings(newSelections);
            updateDrawings(newSelections);
        }
    };

    const handleMoveDown = (index) => {
        if (index < validDrawings.length - 1) {
            const newSelections = update(validDrawings, {
                $splice: [[index, 2, validDrawings[index + 1], validDrawings[index]]],
            });
            setValidDrawings(newSelections);
            updateDrawings(newSelections);
        }
    };

    const handleDeleteFretboard = (index) => {
        const newSelections = update(validDrawings, {
            $splice: [[index, 1]],
        });
        setValidDrawings(newSelections);
        updateDrawings(newSelections);
    };

    const handleEditFretboard = (index, drawing) => {
        //update validDrawings by replacing the drawing at index with the new drawing
        const newSelections = update(validDrawings, {
            $splice: [[index, 1, drawing]],
        });
        setValidDrawings(newSelections);
        updateDrawings(newSelections);
    };


    const handleCancelEdits = () => {
        setIsEditing(-1);
        cancelReplaceSelectedFrets();
    }

    const handleUpdateDiagram = (index) => {
        setIsEditing(-1);
        updateDrawing(index);
    }

    const handleCloseDialog = () => {
        setIsEditing(-1);
        setReturnData(null);
        setOpenDialog(false);
    }


    return (
        <Grid id="outerFretboardMenu" container sx={{
            display: "flex",
            flexDirection: "column",

            margin: { xs: "1px", md: "1px" },

            padding: { xs: "2px", md: "2px" },
            width: { xs: "100%", md: "100%" },
        }} >

            <Grid id="innerFmButton" container spacing={2} sx={{ display: "flex", flexDirection: "row", marginTop: "inherit", marginLeft: "inherit", width: "100%" }}>
                {
                    !noEdit && (
                        <Box>
                            <Button variant="contained" onClick={handleSaveButtonClick} startIcon={<AddIcon />} id="add-diagram-button" size="small" sx={{ marginRight: '5px' }}>
                                Save to Alternates
                            </Button>
                            <Button variant="outlined" onClick={() => handleDownloadImage()} startIcon={<CloudDownloadIcon />} size="small" sx={{ marginRight: '5px' }}>
                                Download Image
                            </Button>
                            {validDrawings.length > 0 &&
                                <Button value="animate" onClick={() => handleAnimateToggle()} disabled={validDrawings.length < 2} size="small" variant={showAnimation ? "contained" : "outlined"} >Animate</Button>
                            }
                        </Box>
                    )
                }

            </Grid>
            {showAnimation && <SVGAnimationComponent drawings={validDrawings} noEdit={noEdit} width={width} height={height} backgroundColor={backgroundColor} />}
            {
                validDrawings.length !== 0 && showAlternates &&
                <Typography variant="h4" sx={styles.fretboardHeadingStyle}> Alternate Diagrams:</Typography>
            }
            {
                showAlternates &&
                <Box
                    sx={{
                        backgroundColor: "#FFF",
                        borderColor: "#1a578e",
                        borderWidth: "2px",
                        borderStyle: "solid",
                        borderRadius: "8px",
                        padding: { xs: "2px", md: "8px" },

                    }}
                    id="drawings-container" >
                    {validDrawings.length === 0 &&
                        <>
                            <Typography variant="h5" gutterBottom align="center">
                                No Alternate Diagrams Yet!
                            </Typography>
                            <Typography variant="body1" gutterBottom>
                                Looks like you haven't added any alternate fretboard diagrams yet. Don't worry, creating and adding alternate diagrams is a breeze and opens up a whole new world of possibilities for your guitar practice or teaching. Here's how to get started:
                            </Typography>
                            <Typography variant="body1" component="div">
                                1. <strong>Create a Diagram:</strong> Begin by selecting chords or notes on the fretboard to create your first diagram. This could be anything from a simple chord to a more complex scale pattern.
                            </Typography>
                            <Typography variant="body1" component="div">
                                2. <strong>Customize It:</strong> Change colors, add notes, or customize markers to make your diagram stand out and meet your specific needs.
                            </Typography>
                            <Typography variant="body1" component="div">
                                3. <strong>Save to Alternates:</strong> Once you're happy with your creation, save it to your collection of alternate diagrams. This is where the magic happens!
                            </Typography>
                            <Typography variant="body1">
                                As you add more diagrams, you'll be able to animate them, adjust timing, and create a visual representation of chord progressions or scales. So go ahead, give it a try and see your guitar knowledge come to life!
                            </Typography>
                        </>}
                    {

                        validDrawings.map((drawing, index) => (
                            <Box sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                flexDirection: "row",
                                backgroundColor: "#f5f5f5 ",
                                borderColor: "#1a578e",
                                borderWidth: "2px",
                                borderStyle: "solid",
                                borderRadius: "8px",
                                padding: { xs: "2px", md: "2px" },
                                margin: { xs: "2px", md: "2px" },
                            }}>
                                <FretboardCard
                                    key={index}
                                    drawing={drawing}
                                    index={index}
                                    position={position}
                                    diagramCardStyle={styles.diagramCardStyle}
                                    diagramCardContentStyle={styles.diagramCardContentStyle}
                                    handleCardClick={handleCardClick}
                                />

                                {!noEdit && (
                                    <Box id="fmCardControls" sx={{ display: "flex", flexDirection: { md: "row", lg: "row" } }}>
                                        <Stack id="fmReorderStack" direction={"row"} spacing={1} sx={{
                                            alignItems: "center",
                                            display: "flex",
                                            flexDirection: "column",
                                            justifyContent: "center",
                                            paddingTop: "8px",
                                        }}>
                                            {index !== 0 && (
                                                <IconButton onClick={() => handleMoveUp(index)}>
                                                    <KeyboardDoubleArrowUpIcon color="secondary" />
                                                </IconButton>
                                            )}
                                            {index !== validDrawings.length - 1 && (
                                                <IconButton onClick={() => handleMoveDown(index)}>
                                                    <KeyboardDoubleArrowDownIcon color="secondary" />
                                                </IconButton>
                                            )}
                                            <IconButton aria-label="add to favorites">
                                                <HighlightOffIcon
                                                    onClick={() => handleDeleteFretboard(index)}
                                                    color="error"
                                                />
                                            </IconButton>

                                        </Stack>
                                        <Stack id="fmTimingStack" sx={{ display: "flex", justifyContent: "center" }}>
                                            <UpdateNoteTiming index={index} drawing={drawing} handleEditFretboard={handleEditFretboard} />
                                            {noEdit && (
                                                <Box sx={{ ml: 1, display: "block", alignItems: "center" }}>
                                                    <Typography variant="h6" sx={{ color: "#1a578e" }} gutterBottom>{index + 1}</Typography>
                                                    <ShowNoteTiming noteValue={drawing.timing} />
                                                </Box>
                                            )}
                                            {isEditing !== index ?
                                                <Button variant="outlined" onClick={() => handleCardClick(drawing, index)} disabled={isEditing !== -1}>
                                                    Edit Diagram
                                                </Button>
                                                :
                                                <Box>
                                                    <Button variant="outlined" onClick={() => handleUpdateDiagram(index)}>
                                                        Update Diagram
                                                    </Button>
                                                    <Button variant="outlined" onClick={handleCancelEdits}>
                                                        Cancel Edits
                                                    </Button>
                                                </Box>
                                            }
                                        </Stack>
                                    </Box>
                                )}




                            </Box>
                        ))

                    }
                </Box>
            }
        </ Grid >

    );
}

export default FretboardMenu;
